import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ReactECharts from "echarts-for-react";
import {
  Users,
  Clock,
  CalendarOff,
  ClipboardCheck,
  Timer,
  DollarSign,
  TrendingUp,
  Activity,
  CheckCircle,
  XCircle,
  Bell,
  LogOut,
  LayoutDashboard,
  FileText,
  Calendar,
  Briefcase,
  Building,
  FolderKanban,
  UserCog,
  CreditCard,
  GraduationCap,
  Target,
  Handshake,
  Shield,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  MapPin,
  UserCheck,
  Wallet,
  BookOpen,
  FileCheck,
  BadgeAlert,
  Zap,
  Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Sidebar Navigation Structure
const sidebarNav = [
  { 
    id: "dashboard", 
    path: "/admin", 
    label: "Dashboard Home", 
    icon: LayoutDashboard 
  },
  {
    id: "projects",
    label: "Project Portfolio",
    icon: FolderKanban,
    children: [
      { path: "/admin/projects/active", label: "Active Projects" },
      { path: "/admin/projects/completed", label: "Completed Projects" },
      { path: "/admin/projects/on-hold", label: "On-Hold Projects" },
      { path: "/admin/projects/risk", label: "Risk Projects" },
    ]
  },
  {
    id: "hr",
    label: "HR Management",
    icon: UserCog,
    children: [
      { path: "/admin/employees", label: "Employee Directory" },
      { path: "/admin/recruitment", label: "Recruitment" },
      { path: "/admin/contracts", label: "Contracts" },
      { path: "/admin/performance", label: "Performance Reviews" },
      { path: "/admin/disciplinary", label: "Disciplinary Records" },
      { path: "/admin/leave", label: "Leave Management" },
      { path: "/admin/overtime", label: "Overtime Requests" },
      { path: "/admin/timesheets", label: "Timesheets" },
    ]
  },
  {
    id: "payroll",
    label: "Payroll",
    icon: CreditCard,
    children: [
      { path: "/admin/payroll/structures", label: "Salary Structures" },
      { path: "/admin/payroll/payslips", label: "Payslips" },
      { path: "/admin/payroll/tax", label: "Tax Deductions" },
      { path: "/admin/payroll/benefits", label: "Benefits" },
      { path: "/admin/payroll/pension", label: "Pension / UIF / PAYE" },
    ]
  },
  {
    id: "training",
    label: "Training & Skills",
    icon: GraduationCap,
    children: [
      { path: "/admin/training/programs", label: "Training Programs" },
      { path: "/admin/training/certifications", label: "Certifications" },
      { path: "/admin/training/skills-matrix", label: "Skills Matrix" },
      { path: "/admin/training/compliance", label: "Compliance Training" },
    ]
  },
  {
    id: "pgmo",
    label: "PGMO Integration",
    icon: Target,
    children: [
      { path: "/admin/pgmo/milestones", label: "Project Milestones" },
      { path: "/admin/pgmo/wbs", label: "Work Breakdown Structure" },
      { path: "/admin/pgmo/resources", label: "Resource Allocation" },
      { path: "/admin/pgmo/budget", label: "Budget vs HR Cost" },
    ]
  },
  {
    id: "crm",
    label: "CRM Integration",
    icon: Handshake,
    children: [
      { path: "/admin/crm/clients", label: "Client Projects" },
      { path: "/admin/crm/escalations", label: "Client Escalations" },
      { path: "/admin/crm/sla", label: "Service Level Agreements" },
    ]
  },
  {
    id: "compliance",
    label: "Compliance & Governance",
    icon: Shield,
    children: [
      { path: "/admin/compliance/hr", label: "HR Compliance" },
      { path: "/admin/compliance/safety", label: "Safety Compliance" },
      { path: "/admin/compliance/labour", label: "Labour Law Tracker" },
      { path: "/admin/compliance/audit", label: "Audit Logs" },
    ]
  },
  {
    id: "approvals",
    path: "/admin/approvals",
    label: "Approvals Center",
    icon: ClipboardCheck,
    badge: true
  },
];

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(["hr"]);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("cdace_token");
  const adminUser = JSON.parse(localStorage.getItem("cdace_admin_user") || "{}");

  const [staffHeatmap, setStaffHeatmap] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Seed HR data first
      await axios.post(`${API}/admin/seed`);
      await axios.post(`${API}/admin/seed-extended`);
      
      const [dashboardRes, activitiesRes, approvalsRes, heatmapRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/activity-feed`, { headers }),
        axios.get(`${API}/admin/pending-approvals`, { headers }),
        axios.get(`${API}/admin/staff-heatmap`, { headers })
      ]);
      
      setDashboardData(dashboardRes.data);
      setActivities(activitiesRes.data.activities);
      setPendingApprovals(approvalsRes.data);
      setStaffHeatmap(heatmapRes.data);
      
      // Generate notifications
      const notifs = [];
      if (approvalsRes.data.total_pending > 10) {
        notifs.push({ type: "warning", message: `${approvalsRes.data.total_pending} items awaiting approval` });
      }
      if (dashboardRes.data.kpis?.budget_burn_rate > 70) {
        notifs.push({ type: "danger", message: "Budget burn rate exceeding 70%" });
      }
      setNotifications(notifs);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 401) {
        navigate("/admin-login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleApproval = async (type, id, action) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = type === 'timesheet' ? 'timesheets' : type === 'leave' ? 'leave-requests' : 'overtime';
      await axios.put(`${API}/admin/${endpoint}/${id}/approve`, { action }, { headers });
      toast.success(`${type} ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} ${type}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("cdace_token");
    localStorage.removeItem("cdace_admin_user");
    navigate("/login");
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val?.toFixed(0) || 0}`;
  };

  // Africa Heatmap Chart
  const africaHeatmapOptions = {
    backgroundColor: 'transparent',
    title: {
      text: 'Staff Distribution Across Africa',
      left: 'center',
      top: 0,
      textStyle: { color: '#94a3b8', fontSize: 14 }
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    visualMap: {
      min: 0,
      max: 5,
      left: 'left',
      top: 'bottom',
      text: ['High', 'Low'],
      calculable: true,
      inRange: { color: ['#1e3a5f', '#c9a227'] },
      textStyle: { color: '#94a3b8' }
    },
    series: [{
      type: 'scatter',
      coordinateSystem: 'geo',
      data: [
        { name: 'South Africa', value: [28, -26, 4] },
        { name: 'Nigeria', value: [8, 9, 2] },
        { name: 'Kenya', value: [37, -1, 2] },
        { name: 'Egypt', value: [31, 26, 2] },
        { name: 'Ghana', value: [-1, 7, 1] },
        { name: 'Senegal', value: [-14, 14, 1] },
      ],
      symbolSize: (val) => Math.max(val[2] * 12, 15),
      itemStyle: { color: '#c9a227' },
      label: {
        show: true,
        formatter: '{b}',
        position: 'right',
        color: '#94a3b8',
        fontSize: 10
      }
    }],
    geo: {
      map: 'africa',
      roam: false,
      zoom: 1.2,
      itemStyle: { areaColor: '#1e293b', borderColor: '#334155' },
      emphasis: { itemStyle: { areaColor: '#374151' } },
      regions: [
        { name: 'South Africa', itemStyle: { areaColor: '#c9a227' } },
        { name: 'Nigeria', itemStyle: { areaColor: '#b8941f' } },
        { name: 'Kenya', itemStyle: { areaColor: '#a78417' } },
        { name: 'Egypt', itemStyle: { areaColor: '#a78417' } },
        { name: 'Ghana', itemStyle: { areaColor: '#967410' } },
        { name: 'Senegal', itemStyle: { areaColor: '#856408' } },
      ]
    }
  };

  // Staff Utilization Gauge
  const utilizationChartOptions = {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min: 0,
      max: 100,
      radius: '100%',
      pointer: { show: true, length: '55%', width: 5, itemStyle: { color: '#c9a227' } },
      axisLine: { lineStyle: { width: 12, color: [[0.3, '#ef4444'], [0.7, '#eab308'], [1, '#22c55e']] } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: { valueAnimation: true, fontSize: 24, color: '#c9a227', offsetCenter: [0, '30%'], formatter: '{value}%' },
      data: [{ value: dashboardData?.kpis?.utilization || 0 }]
    }]
  };

  // Budget Burn Rate
  const budgetBurnOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: { type: 'category', data: ['Allocated', 'Consumed', 'Remaining'], axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8' } },
    yAxis: { type: 'value', max: 100, axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8', formatter: '{value}%' } },
    series: [{
      type: 'bar',
      data: [
        { value: 100, itemStyle: { color: '#3b82f6' } },
        { value: dashboardData?.kpis?.budget_burn_rate || 0, itemStyle: { color: dashboardData?.kpis?.budget_burn_rate > 85 ? '#ef4444' : dashboardData?.kpis?.budget_burn_rate > 70 ? '#eab308' : '#22c55e' } },
        { value: 100 - (dashboardData?.kpis?.budget_burn_rate || 0), itemStyle: { color: '#6b7280' } }
      ],
      barWidth: '40%',
      itemStyle: { borderRadius: [4, 4, 0, 0] }
    }]
  };

  // HR Cost Per Project
  const hrCostPerProjectOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8' } },
    yAxis: { type: 'category', data: ['Limpopo Irrigation', 'Ashanti Gold', 'Nairobi Hospital', 'Cairo Solar'], axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8', fontSize: 10 } },
    series: [{
      type: 'bar',
      data: [85, 72, 45, 38],
      itemStyle: { color: '#c9a227', borderRadius: [0, 4, 4, 0] },
      label: { show: true, position: 'right', color: '#94a3b8', formatter: '${c}K' }
    }]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Command Center...</div>
      </div>
    );
  }

  const { kpis, employees } = dashboardData || {};

  // Calculate additional stats
  const countryCounts = {};
  employees?.forEach(e => {
    countryCounts[e.country] = (countryCounts[e.country] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-[#0b1c2d]" data-testid="admin-dashboard">
      {/* Left Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-slate-950/95 backdrop-blur-xl border-r border-white/5 z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 overflow-y-auto`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5 sticky top-0 bg-slate-950/95 z-10">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#c9a227] flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#0b1c2d]" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-sm">Command Center</h1>
              <p className="text-slate-500 text-xs">CDACE–ClemTech</p>
            </div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {sidebarNav.map((item) => {
            const Icon = item.icon;
            const isExpanded = expandedMenus.includes(item.id);
            const hasChildren = item.children && item.children.length > 0;
            const isActive = item.path && location.pathname === item.path;
            
            return (
              <div key={item.id}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleMenu(item.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-sm transition-all text-slate-400 hover:text-white hover:bg-white/5`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <span className="text-sm">{item.label}</span>
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`block px-3 py-2 text-sm rounded-sm transition-all ${
                              location.pathname === child.path
                                ? "text-[#c9a227] bg-[#c9a227]/10"
                                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-sm transition-all ${
                      isActive ? "text-[#c9a227] bg-[#c9a227]/10 border-l-2 border-[#c9a227]" : "text-slate-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.label}</span>
                    </div>
                    {item.badge && pendingApprovals?.total_pending > 0 && (
                      <Badge className="bg-red-500 text-white">{pendingApprovals.total_pending}</Badge>
                    )}
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5 bg-slate-950/95">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-3">
            <Building className="w-4 h-4" />
            Back to PGMO Portal
          </Link>
          <button onClick={handleLogout} className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm w-full">
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-72">
        {/* Top Bar */}
        <header className="sticky top-0 h-16 bg-slate-950/80 backdrop-blur-md border-b border-white/5 z-40 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Administrator Command Center
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative text-slate-400 hover:text-white">
              <Bell className="w-5 h-5" />
              {(pendingApprovals?.total_pending > 0 || notifications.length > 0) && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                  {pendingApprovals?.total_pending || notifications.length}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                <span className="text-[#c9a227] text-sm font-semibold">{adminUser.name?.charAt(0) || "A"}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm text-white">{adminUser.name || "Admin"}</p>
                <p className="text-xs text-slate-500 capitalize">{adminUser.role?.replace("_", " ") || "Super Admin"}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-4 lg:p-6">
          {/* Risk Alerts Banner */}
          {(notifications.length > 0 || dashboardData?.kpis?.budget_burn_rate > 70) && (
            <div className="mb-6 space-y-2">
              {dashboardData?.kpis?.budget_burn_rate > 85 && (
                <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400">Critical: Budget burn rate at {dashboardData.kpis.budget_burn_rate}% - Immediate attention required</span>
                </div>
              )}
              {dashboardData?.kpis?.budget_burn_rate > 70 && dashboardData?.kpis?.budget_burn_rate <= 85 && (
                <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400">Warning: Budget burn rate at {dashboardData.kpis.budget_burn_rate}%</span>
                </div>
              )}
            </div>
          )}

          {/* Executive Summary KPIs - Row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <Card className="glass-card">
              <CardContent className="p-3">
                <Users className="w-6 h-6 text-[#c9a227] mb-1" />
                <p className="text-2xl font-bold text-[#c9a227]">{kpis?.total_employees || 0}</p>
                <p className="text-xs text-slate-400">Total Staff</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <UserCheck className="w-6 h-6 text-green-400 mb-1" />
                <p className="text-2xl font-bold text-green-400">{kpis?.active_today || 0}</p>
                <p className="text-xs text-slate-400">Active Today</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <CalendarOff className="w-6 h-6 text-yellow-400 mb-1" />
                <p className="text-2xl font-bold text-yellow-400">{kpis?.on_leave_today || 0}</p>
                <p className="text-xs text-slate-400">On Leave</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <Timer className="w-6 h-6 text-purple-400 mb-1" />
                <p className="text-2xl font-bold text-purple-400">{kpis?.hours_logged_today || 0}h</p>
                <p className="text-xs text-slate-400">OT This Week</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <ClipboardCheck className="w-6 h-6 text-red-400 mb-1" />
                <p className="text-2xl font-bold text-red-400">{kpis?.pending_approvals || 0}</p>
                <p className="text-xs text-slate-400">Pending</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <Wallet className="w-6 h-6 text-blue-400 mb-1" />
                <p className="text-2xl font-bold text-blue-400">{formatCurrency(kpis?.hr_cost_month || 0)}</p>
                <p className="text-xs text-slate-400">Payroll MTD</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <TrendingUp className="w-6 h-6 text-orange-400 mb-1" />
                <p className="text-2xl font-bold text-orange-400">{kpis?.budget_burn_rate || 0}%</p>
                <p className="text-xs text-slate-400">Budget Burn</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-3">
                <Activity className="w-6 h-6 text-cyan-400 mb-1" />
                <p className="text-2xl font-bold text-cyan-400">{kpis?.utilization || 0}%</p>
                <p className="text-xs text-slate-400">Utilization</p>
              </CardContent>
            </Card>
          </div>

          {/* Status Indicators */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Card className="glass-card bg-red-500/5 border-red-500/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                <div>
                  <p className="text-red-400 font-semibold">2 Risk Alerts</p>
                  <p className="text-xs text-slate-500">Contract expiring, Over-allocation</p>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card bg-yellow-500/5 border-yellow-500/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div>
                  <p className="text-yellow-400 font-semibold">3 Compliance Alerts</p>
                  <p className="text-xs text-slate-500">Certifications expiring soon</p>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card bg-green-500/5 border-green-500/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div>
                  <p className="text-green-400 font-semibold">6 Projects Compliant</p>
                  <p className="text-xs text-slate-500">All governance checks passed</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Africa Heatmap */}
            <Card className="glass-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-[#c9a227]" />
                    Live Staff Distribution - Africa
                  </span>
                  {staffHeatmap && (
                    <span className="text-xs text-slate-500">
                      Last updated: {new Date(staffHeatmap.last_updated).toLocaleTimeString()}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Totals Summary */}
                {staffHeatmap && (
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-slate-800/50 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-[#c9a227]">{staffHeatmap.totals.total_staff}</p>
                      <p className="text-xs text-slate-400">Total Staff</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-green-400">{staffHeatmap.totals.total_active}</p>
                      <p className="text-xs text-slate-400">Active</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-yellow-400">{staffHeatmap.totals.total_on_leave}</p>
                      <p className="text-xs text-slate-400">On Leave</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">{staffHeatmap.totals.countries_covered}</p>
                      <p className="text-xs text-slate-400">Countries</p>
                    </div>
                  </div>
                )}
                
                {/* Country Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {staffHeatmap?.heatmap.map((country) => (
                    <div 
                      key={country.country}
                      className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-sm p-3 border border-[#c9a227]/10 hover:border-[#c9a227]/30 transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full animate-pulse"
                            style={{ 
                              backgroundColor: `rgba(201, 162, 39, ${0.4 + (country.total_staff / 5) * 0.15})`
                            }}
                          />
                          <span className="text-white font-medium text-sm">{country.country}</span>
                        </div>
                        <span className="text-xs text-slate-500">{country.country_code}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-400">Staff:</span>
                          <span className="text-[#c9a227] ml-1 font-semibold">{country.total_staff}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Active:</span>
                          <span className="text-green-400 ml-1">{country.active_staff}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Utilization:</span>
                          <span className="text-blue-400 ml-1">{country.utilization}%</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Avg Hrs:</span>
                          <span className="text-purple-400 ml-1">{country.avg_hours_today}h</span>
                        </div>
                      </div>
                      {/* Department breakdown */}
                      <div className="mt-2 pt-2 border-t border-slate-700/50">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(country.departments || {}).slice(0, 3).map(([dept, count]) => (
                            <span key={dept} className="text-[10px] bg-slate-700/50 px-1.5 py-0.5 rounded text-slate-300">
                              {dept}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Map Visual */}
                <div className="h-[200px] bg-slate-900/50 rounded-sm flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-30">
                    <svg viewBox="0 0 400 300" className="w-full h-full">
                      <path d="M200,30 L280,60 L320,120 L310,200 L260,270 L140,270 L90,200 L80,120 L120,60 Z" fill="none" stroke="#c9a227" strokeWidth="1"/>
                      {/* Plot points for each country */}
                      {staffHeatmap?.heatmap.map((country, idx) => {
                        const positions = {
                          "South Africa": { x: 200, y: 240 },
                          "Nigeria": { x: 160, y: 140 },
                          "Kenya": { x: 280, y: 160 },
                          "Egypt": { x: 260, y: 60 },
                          "Ghana": { x: 130, y: 150 },
                          "Senegal": { x: 100, y: 120 },
                          "Zimbabwe": { x: 230, y: 200 },
                        };
                        const pos = positions[country.country] || { x: 200 + idx * 20, y: 150 };
                        const size = Math.max(8, country.total_staff * 4);
                        return (
                          <g key={country.country}>
                            <circle 
                              cx={pos.x} 
                              cy={pos.y} 
                              r={size} 
                              fill="#c9a227" 
                              opacity={0.6}
                              className="animate-pulse"
                            />
                            <circle cx={pos.x} cy={pos.y} r={3} fill="#fff" />
                            <text x={pos.x + size + 5} y={pos.y + 4} fill="#94a3b8" fontSize="10">
                              {country.total_staff}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                  <div className="absolute bottom-2 left-2 text-xs text-slate-500">
                    Real-time staff location data • Auto-refreshes every 30s
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Right Panel - Notifications & Quick Actions */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#c9a227]" />
                  Live Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[380px] overflow-y-auto">
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                  <div className="flex items-center gap-2 text-red-400 text-sm font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    Contract Expiring
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Peter Ndlovu - 30 days remaining</p>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
                  <div className="flex items-center gap-2 text-yellow-400 text-sm font-semibold">
                    <BadgeAlert className="w-4 h-4" />
                    Certification Expiry
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Safety cert - 3 staff affected</p>
                </div>
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                  <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                    <FileCheck className="w-4 h-4" />
                    New Onboarding
                  </div>
                  <p className="text-xs text-slate-400 mt-1">2 new hires pending document upload</p>
                </div>
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm">
                  <div className="flex items-center gap-2 text-purple-400 text-sm font-semibold">
                    <BookOpen className="w-4 h-4" />
                    Training Required
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Compliance training due - 4 staff</p>
                </div>
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-sm">
                  <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                    <CheckCircle className="w-4 h-4" />
                    Performance Review
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Q1 reviews completed - 8/12</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 text-sm">Staff Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <ReactECharts option={utilizationChartOptions} style={{ height: '180px' }} />
                <p className="text-center text-slate-400 text-xs">
                  {kpis?.utilization >= 80 ? 'Excellent' : kpis?.utilization >= 60 ? 'Good' : 'Needs Improvement'}
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 text-sm">Budget Burn Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <ReactECharts option={budgetBurnOptions} style={{ height: '180px' }} />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 text-sm">HR Cost per Project</CardTitle>
              </CardHeader>
              <CardContent>
                <ReactECharts option={hrCostPerProjectOptions} style={{ height: '180px' }} />
              </CardContent>
            </Card>
          </div>

          {/* Live Activity Feed & Approval Queue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Live Activity Feed */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#c9a227]" />
                  Live Activity Feed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[350px] overflow-y-auto">
                {activities.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-sm hover:bg-slate-800/50 transition-colors">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type.includes('approved') ? 'bg-green-400' :
                      activity.type.includes('rejected') ? 'bg-red-400' :
                      activity.type.includes('submitted') ? 'bg-blue-400' :
                      activity.type.includes('expir') ? 'bg-yellow-400' : 'bg-purple-400'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm text-slate-300">{activity.message}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Pending Approval Queue */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-200 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-[#c9a227]" />
                    Pending Approvals
                  </span>
                  <Badge className="bg-red-500/20 text-red-400">{pendingApprovals?.total_pending || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[350px] overflow-y-auto">
                {pendingApprovals?.timesheets?.slice(0, 3).map((ts) => (
                  <div key={ts.id} className="p-3 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400">Timesheet</Badge>
                        <span className="text-white text-sm">{ts.user_name}</span>
                      </div>
                      <span className="text-xs text-slate-500">{ts.work_date}</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-2">{ts.hours_worked}h - {ts.task_description?.slice(0, 40)}...</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval('timesheet', ts.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-3 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />Approve
                      </Button>
                      <Button size="sm" onClick={() => handleApproval('timesheet', ts.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-3 text-xs">
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingApprovals?.leave_requests?.slice(0, 2).map((lr) => (
                  <div key={lr.id} className="p-3 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-yellow-500/20 text-yellow-400">Leave</Badge>
                        <span className="text-white text-sm">{lr.user_name}</span>
                      </div>
                      <span className="text-xs text-slate-500">{lr.leave_type}</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-2">{lr.start_date} to {lr.end_date}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval('leave', lr.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-3 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />Approve
                      </Button>
                      <Button size="sm" onClick={() => handleApproval('leave', lr.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-3 text-xs">
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingApprovals?.overtime?.slice(0, 2).map((ot) => (
                  <div key={ot.id} className="p-3 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-500/20 text-purple-400">Overtime</Badge>
                        <span className="text-white text-sm">{ot.user_name}</span>
                      </div>
                      <span className="text-xs text-slate-500">{ot.hours}h @ {ot.overtime_rate}x</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-2">{ot.reason?.slice(0, 40)}...</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval('overtime', ot.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-3 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />Approve
                      </Button>
                      <Button size="sm" onClick={() => handleApproval('overtime', ot.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-3 text-xs">
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
                {(pendingApprovals?.total_pending || 0) > 7 && (
                  <Link to="/admin/approvals" className="block text-center text-[#c9a227] hover:underline text-sm py-2">
                    View all {pendingApprovals.total_pending} pending items →
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
